import { makeAutoObservable, runInAction } from "mobx";
import ProfilPlayer from "./ProfilPlayer.js";

const API_URL = import.meta.env.VITE_API_URL;

export default class ProfilPlayerStore {
    _profilPlayerList = [];
    _isLoaded = { profilPlayer: false };
    _loadingPromise = null;

    constructor() {
        makeAutoObservable(this);
    }

    get profilPlayerList() {
        return this._profilPlayerList;
    }

    get profilPlayerLoadedNumber() {
        return this._profilPlayerList.length;
    }

    // Charge plusieurs profils (jusqu’à targetTotal)
    loadProfilPlayer(targetTotal = 30) {
        if (this._profilPlayerList.length >= targetTotal) {
            return Promise.resolve();
        }

        if (this._loadingPromise) {
            return this._loadingPromise.then(() => this.loadProfilPlayer(targetTotal));
        }

        const missingCount = targetTotal - this._profilPlayerList.length;
        const start = this._profilPlayerList.length;
        const end = start + missingCount;

        this._loadingPromise = this._fetchProfilPlayers(start, end).finally(() => {
            this._loadingPromise = null;
        });

        return this._loadingPromise;
    }

    resetProfilPlayerList() {
        this._profilPlayerList = [];
        this._isLoaded = { profilPlayer: false };
        this._loadingPromise = null;
    }

    getProfilPlayerById(id) {
        return this._profilPlayerList.find((profilPlayer) => profilPlayer.id === id);
    }

    async _fetchProfilPlayers(start, end) {
        try {
            const response = await fetch(`${API_URL}/profilList?start=${start}&end=${end}`);
            const data = await response.json();

            runInAction(() => {
                const newPlayers = data.map((profilPlayer) =>
                    new ProfilPlayer({
                        id: Number(profilPlayer.id),
                        pseudo: profilPlayer.pseudo,
                        score: Number(profilPlayer.score),
                        profilPicture: profilPlayer.profilPicture,
                        theme: JSON.parse(profilPlayer.theme),
                    })
                );

                this._profilPlayerList.push(...newPlayers);
                this._isLoaded.profilPlayer = true;
            });
        } catch (error) {
            console.error("Erreur lors du chargement des profils :", error);
        }
    }

    /**
     * Charge un profil par ID depuis le store ou via l'API.
     * @param {number|string} id
     * @returns {Promise<ProfilPlayer|null>}
     */
    async fetchProfilById(id) {
        const numericId = Number(id);

        const existingProfil = this.getProfilPlayerById(numericId);
        if (existingProfil) {
            return existingProfil;
        }

        try {
            const response = await fetch(`${API_URL}/profil?id=${numericId}`);
            if (!response.ok) throw new Error("Profil introuvable");

            const profilData = await response.json();

            const newProfil = new ProfilPlayer({
                id: Number(profilData.id),
                pseudo: profilData.pseudo,
                score: Number(profilData.score),
                profilPicture: profilData.profilPicture,
                theme: JSON.parse(profilData.theme),
            });

            runInAction(() => {
                this._profilPlayerList.push(newProfil);
            });

            return newProfil;
        } catch (error) {
            console.error(`Erreur lors du chargement du profil ID ${numericId} :`, error);
            return null;
        }
    }
}
